import { useCallback, useMemo } from 'react';
import {
	PublicKey,
	TransactionInstruction,
	VersionedTransaction,
} from '@solana/web3.js';
import { notify } from 'src/utils/notifications';
import useCurrentAuthority from '../../useCurrentAuthority';
import useDriftAccountStore, {
	AccountData,
} from 'src/stores/useDriftAccountsStore';
import { MAIN_POOL_ID } from '@velocity-exchange/common';
import useDriftClient from '../../useDriftClient';
import useDriftClientIsReady from '../../useDriftClientIsReady';
import { useGetTxParams } from '../../driftActions/transactions/useGetTxParams';
import { DEFAULT_PUBLIC_KEY } from 'src/@types/types';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import chunk from 'lodash/chunk';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignDriftActionWithFallback } from 'src/hooks/wallets/useSponsoredSignDriftActionWithFallback';

const MAX_ACCOUNTS_PER_TX = 8;

const delegateableFilter = (account: AccountData) => {
	return (
		account.delegate.equals(DEFAULT_PUBLIC_KEY) &&
		account.poolId === MAIN_POOL_ID
	);
};

const DEFAULT_SUBACCOUNTS: AccountData[] = [];

export const useSubAccountsDelegation = () => {
	const authority = useCurrentAuthority();
	const driftClient = useDriftClient();
	const driftClientIsReady = useDriftClientIsReady();
	const accounts = useDriftAccountStore((s) => s.accounts);
	const currentUserAccount = useCurrentUserAccount();

	const getAccountsStore = useDriftAccountStore((s) => s.get);
	const getTxParams = useGetTxParams();

	const allAuthorityAccounts = useMemo(() => {
		if (!authority) return DEFAULT_SUBACCOUNTS;

		return Object.values(accounts)
			.filter((acc): acc is AccountData => acc !== undefined)
			.filter((acc) => acc.authority.equals(authority)); // filters out accounts delegated to connected authority
	}, [authority, accounts]);

	const allUndelegatedAuthorityAccounts = useMemo(() => {
		return allAuthorityAccounts.filter(delegateableFilter);
	}, [allAuthorityAccounts]);

	const isCurrentUserAccountDelegatable =
		!!currentUserAccount && delegateableFilter(currentUserAccount);

	const getAuthorityAccounts = useCallback(() => {
		if (!authority) return [];

		const userAccounts = getAccountsStore().accounts;
		return Object.values(userAccounts)
			.filter((acc) => acc?.authority.equals(authority))
			.filter((acc) => acc !== undefined);
	}, [getAccountsStore, authority]);

	const setupDelegateAction = useCallback(
		async (
			wallet: UiEnhancedIWalletV2,
			newDelegate: PublicKey,
			overrideExistingDelegates: boolean
		) => {
			if (!driftClientIsReady || !driftClient || !authority) return;

			const authorityAccounts = getAuthorityAccounts();

			const delegatableAccounts = overrideExistingDelegates
				? authorityAccounts
				: authorityAccounts.filter(delegateableFilter);

			if (!delegatableAccounts.length) {
				notify({
					type: 'error',
					message: 'No subaccounts to set up delegates for.',
				});
				return;
			}

			const txParams = getTxParams({ useSimulatedComputeUnits: true });

			const buildDelegateIxs = (accounts: AccountData[]) =>
				Promise.all(
					accounts.map((account) =>
						driftClient.getUpdateUserDelegateIx(newDelegate, {
							subAccountId: account.userId,
							userAccountPublicKey: account.pubKey,
							authority: driftClient.wallet.publicKey,
						})
					)
				);

			// Single transaction if 8 or fewer accounts
			if (delegatableAccounts.length <= MAX_ACCOUNTS_PER_TX) {
				const ixs = await buildDelegateIxs(delegatableAccounts);
				return await wallet.signAndSendTransaction(ixs, txParams);
			}

			// Multiple transactions for more than 8 accounts
			const accountChunks = chunk(delegatableAccounts, MAX_ACCOUNTS_PER_TX);

			const instructionsMap: Record<string, TransactionInstruction[]> = {};
			for (const [i, accountChunk] of accountChunks.entries()) {
				instructionsMap[`chunk_${i}`] = await buildDelegateIxs(accountChunk);
			}

			const { signedTxMap } = await wallet.buildAndSignTransactionsMap(
				instructionsMap,
				txParams
			);

			const txSigs = await Promise.all(
				Object.values(signedTxMap)
					.filter(Boolean)
					.map((signedTx) =>
						driftClient.sendTransaction(
							signedTx as VersionedTransaction,
							undefined,
							undefined,
							true
						)
					)
			);

			return txSigs[0]?.txSig;
		},
		[
			driftClientIsReady,
			driftClient,
			authority,
			getAuthorityAccounts,
			getTxParams,
		]
	);

	const wrappedSetupDelegate =
		useSponsoredSignDriftActionWithFallback(setupDelegateAction);

	/**
	 * Sets up the given authority as delegate for all subaccounts of current authority.
	 * If there are more than 8 subaccounts, chunks into multiple transactions and uses signAll.
	 */
	const setupDelegateForSubaccounts = useCallback(
		async (newDelegate: PublicKey, overrideExistingDelegates = false) => {
			try {
				return await wrappedSetupDelegate(
					newDelegate,
					overrideExistingDelegates
				);
			} catch (e) {
				console.error('setupDelegateForAllSubaccounts ~ err:', e);
				throw e;
			}
		},
		[wrappedSetupDelegate]
	);

	const devRemoveAllSubAccountsDelegates = useCallback(async () => {
		if (!driftClientIsReady || !driftClient || !authority) return;

		const authorityAccounts = getAuthorityAccounts();
		const delegatableAccounts = authorityAccounts.filter(
			(acc) => !acc.delegate.equals(DEFAULT_PUBLIC_KEY)
		);

		if (delegatableAccounts.length === 0) {
			notify({
				type: 'error',
				message: 'No subaccounts to remove delegates from.',
			});
			return;
		}

		const ixs = await Promise.all(
			delegatableAccounts.map((account) =>
				driftClient.getUpdateUserDelegateIx(DEFAULT_PUBLIC_KEY, {
					subAccountId: account.userId,
					userAccountPublicKey: account.pubKey,
					authority: driftClient.wallet.publicKey,
				})
			)
		);

		const tx = await driftClient.buildTransaction(
			ixs,
			getTxParams({ useSimulatedComputeUnits: true })
		);
		await driftClient.sendTransaction(tx);

		notify({
			type: 'success',
			message: 'Removed all subaccount delegates.',
		});
	}, [
		driftClient,
		driftClientIsReady,
		authority,
		getAuthorityAccounts,
		getTxParams,
	]);

	return {
		setupDelegateForSubaccounts,
		devRemoveAllSubAccountsDelegates,
		isCurrentUserAccountDelegatable,
		allAuthorityAccounts,
		allUndelegatedAuthorityAccounts,
	};
};
